/**
 * Which kinds of record a Printable includes (ENG-9644).
 *
 * The Printables page CONFIGURES a print job and `/print-reports` RENDERS it,
 * with a URL in between. This module owns the vocabulary both ends share: the
 * four selectable types, what "all of them" means, and how a selection survives
 * the round trip through `?types=`.
 *
 * ## Why "Merits" and not "Points"
 *
 * The Timeline's own type filter already calls these Merits / Demerits /
 * Rewards, and the API flag is literally `includemerits`. A second name for the
 * same thing on a second screen is how two filters end up disagreeing about
 * what they select, so this list is deliberately the same list, in the same
 * words.
 *
 * ## Why hall passes are not a fourth SDK flag
 *
 * Merits, demerits and rewards are three booleans on ONE `getTimeline` request.
 * Hall passes come from campus `/hallpass`, a separate, separately-paginated
 * API. {@link isPointsFamilyType} is the predicate that keeps that split
 * honest: with no points-family type selected there is no `getTimeline` request
 * worth making, because a request that asks for nothing does not return "no
 * matches", it returns an answer to no question.
 *
 * ## RECORD types vs FIELD types
 *
 * Everything above describes types that put a ROW on the page: one merit, one
 * pass, one purchase. `comments` is not one of those. A comment is a FIELD on
 * a record — the note on a merit, the note on a hall pass, both normalised onto
 * `event.comment` by the time they reach the printable — and deselecting it
 * strips that one line while the record itself still prints.
 *
 * The distinction is load-bearing in three places, which is why it lives here
 * as {@link PRINTABLE_RECORD_TYPES} rather than as an `=== 'comments'` check
 * repeated at each call site:
 *
 *  1. **The "at least one" floor.** A report needs at least one RECORD type.
 *     If a field type could satisfy that floor, a user could remove Merits,
 *     Demerits, Rewards and Passes, keep Comments, and reach a report with no
 *     records to hang a comment on — exactly the page of nothing the floor
 *     exists to prevent. See {@link togglePrintableDataType} and
 *     {@link hasPrintableRecordSelection}.
 *  2. **What to fetch.** Neither `getTimeline` nor campus `/hallpass` has a
 *     "comments" endpoint, so a field type must never make
 *     {@link hasPointsFamilySelection} — or the hall-pass check beside it —
 *     think there is something to go and get.
 *  3. **Feature visibility.** Rewards and Passes are licensed features a Site
 *     Leader can switch off. A field type is not a feature and is always
 *     available; see {@link PRINTABLE_FIELD_TYPES}.
 *
 * Both halves stay in ONE selection set and ONE `?types=` param, because to the
 * reader they are one checklist of what goes on the page, and a second param
 * would be a second thing to keep in sync.
 *
 * ## What is SELECTABLE is not fixed (ENG-9803)
 *
 * The five types above are the vocabulary; they are not automatically the menu.
 * Two things narrow what a given user may actually pick:
 *
 *  - **Feature Visibility** (/account) governs Rewards and Passes, the two
 *    licensed features.
 *  - **Demerit visibility** governs Demerits. A school can restrict who sees
 *    demerit data at all, and a viewer resolved to `'none'` must not be offered
 *    a Demerits pill, let alone able to print one.
 *
 * {@link printableDataTypesForDemeritScope} owns the second of those so no call
 * site has to remember that "merits and demerits are the core two" — which was
 * true until this ticket and is not any more.
 */

import type { DemeritScope } from './demeritVisibility';

/**
 * The record types a Printable can include — one ROW on the page each.
 *
 * A report needs at least one of these; see the module header.
 *
 * This is the full VOCABULARY, not the menu: Rewards, Passes and (since
 * ENG-9803) Demerits can each be unavailable to a given viewer. Merits is the
 * one that never is, which is what keeps the "at least one record type" floor
 * satisfiable for every viewer — see
 * {@link printableDataTypesForDemeritScope}.
 */
export const PRINTABLE_RECORD_TYPES = ['merits', 'demerits', 'rewards', 'hallpasses'] as const;

export type PrintableRecordType = (typeof PRINTABLE_RECORD_TYPES)[number];

/**
 * The field types a Printable can include — a LINE ON a record, never a record.
 *
 * Deselecting one of these does not remove any record from the report; it
 * removes that field from every record that has it. `comments` covers positive
 * notes, negative notes and hall-pass notes alike, because all three arrive on
 * the same `event.comment` field (see `printableHallPass.ts`).
 *
 * Field types are NOT licensed features, so callers gating on /account Feature
 * Visibility must treat them as always available.
 */
export const PRINTABLE_FIELD_TYPES = ['comments'] as const;

export type PrintableFieldType = (typeof PRINTABLE_FIELD_TYPES)[number];

/**
 * Everything selectable, in DISPLAY order.
 *
 * This is the wire order for `?types=` and the render order for the pills.
 *
 * Deliberately NOT records-then-fields: Comments sits third, directly after
 * the two point types whose notes it most often carries, because that is where
 * a reader scanning the row expects the thing that annotates them. Grouping by
 * records-vs-fields is a fact about the CODE (what may be fetched, what
 * satisfies the floor); it was never a fact about reading order.
 *
 * Because the order is written out rather than derived, the two assertions
 * below are what stop a future type being added to one list and forgotten
 * here.
 */
export const PRINTABLE_DATA_TYPES = [
  'merits',
  'demerits',
  'comments',
  'rewards',
  'hallpasses',
] as const;

export type PrintableDataType = PrintableRecordType | PrintableFieldType;

// Compile-time completeness, both directions: every record/field type appears
// in the display list, and the display list invents nothing that is neither.
type _EveryTypeIsDisplayed =
  Exclude<PrintableDataType, (typeof PRINTABLE_DATA_TYPES)[number]> extends never ? true : never;
type _EveryDisplayedIsAType =
  Exclude<(typeof PRINTABLE_DATA_TYPES)[number], PrintableDataType> extends never ? true : never;
/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
const _completeness: [_EveryTypeIsDisplayed, _EveryDisplayedIsAType] = [true, true];

/** Human labels, matching the Timeline's type filter word-for-word. */
export const PRINTABLE_DATA_TYPE_LABELS: Record<PrintableDataType, string> = {
  merits: 'Merits',
  demerits: 'Demerits',
  rewards: 'Rewards',
  // "Passes", not "Hall Passes": these labels sit in a row of one-word pills
  // beside Merits / Demerits / Rewards, and the section already reads
  // "Data to Include" on a Hall Pass-aware report. The Timeline's own type
  // filter still says "Hall Passes" — it has a wider dropdown and no sibling
  // pressure. Worth aligning the two if that ever looks inconsistent.
  hallpasses: 'Passes',
  comments: 'Comments',
};

/**
 * The three types that come from one `getTimeline` request.
 *
 * Hall passes are excluded by construction rather than by omission — see the
 * module header.
 */
export const POINTS_FAMILY_TYPES = ['merits', 'demerits', 'rewards'] as const;

const POINTS_FAMILY = new Set<string>(POINTS_FAMILY_TYPES);
const VALID = new Set<string>(PRINTABLE_DATA_TYPES);
const RECORD_TYPES = new Set<string>(PRINTABLE_RECORD_TYPES);

/**
 * Is this type served by `getTimeline` (rather than campus `/hallpass`)?
 *
 * False for every field type: there is no comments endpoint, and a field type
 * that answered true here would provoke a fetch for data nobody can return.
 */
export function isPointsFamilyType(type: PrintableDataType): boolean {
  return POINTS_FAMILY.has(type);
}

/**
 * Does this type put a ROW on the page, rather than a line on someone else's
 * row? See the module header for why the distinction is not inlined.
 */
export function isPrintableRecordType(type: PrintableDataType): type is PrintableRecordType {
  return RECORD_TYPES.has(type);
}

/** Selected RECORD types in declaration order. Field types are excluded. */
export function listPrintableRecordTypes(
  types: ReadonlySet<PrintableDataType>,
): PrintableRecordType[] {
  return PRINTABLE_RECORD_TYPES.filter((type) => types.has(type));
}

/**
 * True when the selection contains something that can actually put a row on the
 * page.
 *
 * The predicate every "is this report worth producing?" guard should use.
 * `types.size > 0` is NOT the same question: a selection of nothing but
 * Comments is non-empty and still prints a blank report.
 */
export function hasPrintableRecordSelection(types: ReadonlySet<PrintableDataType>): boolean {
  return PRINTABLE_RECORD_TYPES.some((type) => types.has(type));
}

/**
 * Every type, selected — records AND fields. The default for a fresh Printable.
 *
 * Field types are included deliberately rather than opted into: comments
 * printed unconditionally before they were selectable, so "everything" has to
 * keep meaning what it meant, or an old link would quietly drop them.
 */
export function allPrintableDataTypes(): Set<PrintableDataType> {
  return new Set(PRINTABLE_DATA_TYPES);
}

/**
 * The types this viewer may print at all, given their demerit scope (ENG-9803).
 *
 * The starting point for a caller's "available" set, which Feature Visibility
 * then narrows further. Callers used to seed that set with the literal
 * `['merits', 'demerits', ...fields]` on the reasoning that merits and demerits
 * were core points nothing could switch off. Demerits no longer is, and a
 * hard-coded pair is exactly the kind of thing that keeps being true in one
 * file after it stops being true in the rules.
 *
 * Only `'none'` removes anything. A `'own'`-scoped teacher KEEPS the Demerits
 * option: they still record demerits and may still print the ones they
 * recorded, and `/print-reports` is what narrows those rows to their own. A
 * `'none'` viewer loses the option outright, because there is no honest subset
 * left to offer them.
 *
 * Returns a fresh, MUTABLE set each call so a caller can delete from it while
 * applying its own gates.
 *
 * @param scope - The viewer's scope, from `useDemeritVisibility()`.
 * @returns Every selectable type this viewer may be offered.
 */
export function printableDataTypesForDemeritScope(scope: DemeritScope): Set<PrintableDataType> {
  const types = new Set<PrintableDataType>(PRINTABLE_DATA_TYPES);
  if (scope === 'none') types.delete('demerits');
  return types;
}

/**
 * Serialize a selection for the print URL.
 *
 * Always emits every selected type explicitly, in declaration order, even when
 * that is all four. An "all selected" state that serializes to nothing would be
 * indistinguishable from a missing param, and the two have to be told apart at
 * the far end for the empty selection to survive.
 */
export function encodePrintableDataTypes(types: ReadonlySet<PrintableDataType>): string {
  return PRINTABLE_DATA_TYPES.filter((type) => types.has(type)).join(',');
}

/**
 * Read a selection back off the print URL.
 *
 * An ABSENT param means "everything": a Printable link made before this option
 * existed, or hand-built, must keep printing the whole report rather than
 * silently narrowing. A PRESENT but empty param is an explicit empty selection
 * and stays empty — the render side shows the "nothing selected" state rather
 * than quietly printing more than was asked for. Unknown values are dropped.
 */
export function decodePrintableDataTypes(
  raw: string | undefined | null,
): Set<PrintableDataType> {
  if (raw == null) return allPrintableDataTypes();
  const selected = new Set<PrintableDataType>();
  for (const part of raw.split(',')) {
    const token = part.trim().toLowerCase();
    if (VALID.has(token)) selected.add(token as PrintableDataType);
  }
  return selected;
}

/**
 * True when at least one `getTimeline`-served type is selected.
 *
 * Field types can never move this: Comments alone leaves nothing to fetch.
 */
export function hasPointsFamilySelection(types: ReadonlySet<PrintableDataType>): boolean {
  return POINTS_FAMILY_TYPES.some((type) => types.has(type));
}

/** Selected types in declaration order — the shape analytics and tests read. */
export function listPrintableDataTypes(
  types: ReadonlySet<PrintableDataType>,
): PrintableDataType[] {
  return PRINTABLE_DATA_TYPES.filter((type) => types.has(type));
}

/**
 * Toggle one type, refusing to leave the report with no records on it.
 *
 * At least one RECORD type must stay on. The alternative behaviours were both worse:
 * allowing an empty set produces a printable with nothing on it, which reads as
 * a broken feature rather than an intentional one; and snapping back to "all
 * four" would silently ADD three types the user was in the middle of removing,
 * which is a surprise in the expanding direction and the one users notice least
 * before hitting print. Refusing the last removal is the only option where the
 * control never shows a state the report can't honour.
 *
 * One function for both directions because the pill control does exactly two
 * things — remove a selected type, add an unselected one — and they are the
 * same operation read in opposite directions.
 *
 * The floor counts RECORD types only. Counting the whole set would let Comments
 * stand in for the last record and hand back a report with a comment column and
 * nothing to comment on. A field type is therefore ALWAYS removable: taking it
 * away subtracts a line from rows that still print.
 *
 * @returns The next selection, or the SAME set reference when the removal was
 *   refused, so a caller can detect the no-op.
 */
export function togglePrintableDataType(
  types: ReadonlySet<PrintableDataType>,
  type: PrintableDataType,
): ReadonlySet<PrintableDataType> {
  if (!types.has(type)) return new Set([...types, type]);
  if (isPrintableRecordType(type) && listPrintableRecordTypes(types).length <= 1) return types;
  const next = new Set(types);
  next.delete(type);
  return next;
}

/**
 * The types NOT currently selected, in declaration order.
 *
 * What the "Add" menu offers. Empty when everything is selected, which is what
 * makes the Add control disappear rather than open an empty menu — same shape
 * as `unusedFilters` / `unusedMenuKeys` on the Hall Pass report filter bars.
 */
export function unselectedPrintableDataTypes(
  types: ReadonlySet<PrintableDataType>,
): PrintableDataType[] {
  return PRINTABLE_DATA_TYPES.filter((type) => !types.has(type));
}
